import { Suspense, useState, useMemo } from "react";
import {
  createFileRoute,
  isRedirect,
  redirect,
  useNavigate,
  useSearch,
  useLocation,
} from "@tanstack/react-router";
import { TradesFilterParams } from "./trades/-components/use-trades";
import { TradesFilters } from "./trades/-components/trades-filters";
import { TradesTable } from "./trades/-components/trades-table";
import { TradesTableLoadingFallback } from "./trades/-components/trades-table-loading";
import { z } from "zod/v4";
import { zodValidator } from "@tanstack/zod-adapter";
import { CardWithHeader } from "@/components/card-with-header";
import { applicationConfigQuery } from "@/lib/queries";
import { ApplicationConfigNotFoundError } from "@/lib/application-api";

const searchParamsSchema = z.object({
  expandedRow: z.string().optional(),
  tradeId: z.string().optional(),
  statuses: z.string().optional(),
  sourceCurrency: z.string().optional(),
  destinationCurrency: z.string().optional(),
  startDateUtc: z.union([z.string(), z.number()]).optional(),
  endDateUtc: z.union([z.string(), z.number()]).optional(),
  minSourceAmount: z.number().optional(),
  maxSourceAmount: z.number().optional(),
  minDestinationAmount: z.number().optional(),
  maxDestinationAmount: z.number().optional(),
});

function toValidDateTimestamp(value: number | string | undefined) {
  if (value === undefined || value === "") return undefined;

  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.getTime() : undefined;
}

export const Route = createFileRoute(
  "/(app)/_main-layout/_exchange/exchange/trades",
)({
  component: TradesPageComponent,
  validateSearch: zodValidator(searchParamsSchema),
  beforeLoad: async ({ context }) => {
    // Guard: redirect to /exchange if FX is not enabled.
    try {
      const config = await context.queryClient.ensureQueryData(
        applicationConfigQuery(
          context.selectedApplicationId,
          context.accessToken,
        ),
      );
      if (!config.enabled) {
        throw redirect({ to: "/exchange" });
      }
    } catch (error) {
      if (isRedirect(error)) throw error;
      if (error instanceof ApplicationConfigNotFoundError) {
        throw redirect({ to: "/exchange" });
      }
      // Non-config errors (network, etc.) — let the component handle them
    }
  },
});

function TradesPageComponent() {
  const search = useSearch({
    from: "/(app)/_main-layout/_exchange/exchange/trades",
  });
  const navigate = useNavigate();
  const location = useLocation();

  // State for available currencies (updated when table loads)
  const [availableCurrencies, setAvailableCurrencies] = useState<{
    source: string[];
    destination: string[];
  }>({
    source: [],
    destination: [],
  });

  // Extract filter params from search
  const filters: TradesFilterParams | undefined = useMemo(() => {
    const startDateUtc = toValidDateTimestamp(search.startDateUtc);
    const endDateUtc = toValidDateTimestamp(search.endDateUtc);
    const hasFilters =
      search.tradeId ||
      search.statuses ||
      search.sourceCurrency ||
      search.destinationCurrency ||
      startDateUtc !== undefined ||
      endDateUtc !== undefined ||
      search.minSourceAmount !== undefined ||
      search.maxSourceAmount !== undefined ||
      search.minDestinationAmount !== undefined ||
      search.maxDestinationAmount !== undefined;

    if (!hasFilters) return undefined;

    return {
      tradeId: search.tradeId,
      statuses: search.statuses,
      sourceCurrency: search.sourceCurrency,
      destinationCurrency: search.destinationCurrency,
      startDateUtc,
      endDateUtc,
      minSourceAmount: search.minSourceAmount,
      maxSourceAmount: search.maxSourceAmount,
      minDestinationAmount: search.minDestinationAmount,
      maxDestinationAmount: search.maxDestinationAmount,
    };
  }, [
    search.tradeId,
    search.statuses,
    search.sourceCurrency,
    search.destinationCurrency,
    search.startDateUtc,
    search.endDateUtc,
    search.minSourceAmount,
    search.maxSourceAmount,
    search.minDestinationAmount,
    search.maxDestinationAmount,
  ]);

  // Toggle row expansion
  const toggleRow = (tradeId: string) => {
    if (search.expandedRow === tradeId) {
      navigate({
        to: location.pathname,
        search: (currentParams) => ({
          ...currentParams,
          expandedRow: undefined,
        }),
      });
      return;
    }
    navigate({
      to: location.pathname,
      search: (currentParams) => ({
        ...currentParams,
        expandedRow: tradeId,
      }),
    });
  };

  return (
    <div className="space-y-6">
      <CardWithHeader
        variant="exchange"
        label="Trade History"
        title="Search and filter your exchange trades"
      >
        <Suspense fallback={<TradesTableLoadingFallback />}>
          <TradesTable
            filters={filters}
            expandedRow={search.expandedRow}
            onRowToggle={toggleRow}
            onCurrenciesAvailable={setAvailableCurrencies}
            renderControls={(rightControl) => (
              <div className="mb-4">
                <TradesFilters
                  availableSourceCurrencies={availableCurrencies.source}
                  availableDestinationCurrencies={
                    availableCurrencies.destination
                  }
                  rightControl={rightControl}
                />
              </div>
            )}
          />
        </Suspense>
      </CardWithHeader>
    </div>
  );
}
